'use client'
import React, { memo, PropsWithChildren, useCallback, useEffect, useMemo } from 'react'
import { AssetSummary, SoundsCrateSound } from '@/lib/assets/types'
import isEqual from 'react-fast-compare'
import { getAssetKey } from '@/lib/assets/assets'
import { Button } from '@/components/ui/button'
import { useQuery } from '@tanstack/react-query'
import client from '@/lib/api/client'
import { cn, formatDuration } from '@/lib/utils'
import styles from './music-bar.module.scss'
import useAudioPlayer from '@/lib/hooks/useAudioPlayer'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowDownToLineIcon, PauseIcon, PlayIcon, HeartIcon } from 'lucide-react'
import { StarSolid } from 'iconoir-react'
import SoundWave from '@/components/assets-explorer/sound-wave'
import { features } from '@/lib/config'
import useFavorites from '@/lib/hooks/useFavorites'
import { HeartFilledIcon } from '@radix-ui/react-icons'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Skeleton } from '@/components/ui/skeleton'
import { useUserContext } from '@/lib/contexts/UserContext'
import Link from 'next/link'
import useAssetDetail from '@/lib/hooks/useAssetDetail'

const downsSampleSignal = (signal: any, factor: number) => {
  factor = Math.max(factor, 1)
  const newLength = Math.floor(signal.length / factor)
  const newSignal = new Array(newLength)

  for (let i = 0; i < newLength; i++) {
    newSignal[i] = signal[i * factor]
  }

  return newSignal
}

export type MusicBarProps = {
  asset: AssetSummary
  stopOnUnmount?: boolean
  hideTitle?: boolean
  hideTools?: boolean
  gridLayout?: boolean
  onClick?: (asset?: AssetSummary) => void
}

const MusicBar = memo(
  ({
    asset,
    children,
    stopOnUnmount = true,
    hideTitle = false,
    hideTools = false,
    gridLayout = false,
  }: PropsWithChildren<MusicBarProps>) => {
    const assetKey = getAssetKey(asset)
    const src = `https://p.productioncrate.com/stock-hd/audio/${assetKey}.mp3`
    const { audioManager, isPlaying, play, stop, seek, currentTime } = useAudioPlayer(src)

    const { data: assetDetails, isLoading: isLoadingAssetDetails } = useQuery({
      queryKey: ['asset', assetKey],
      queryFn: () =>
        client
          .get<SoundsCrateSound>(`/assets/${assetKey}`, {
            params: {
              type: asset.__typename,
              main_category: asset.mainCategorySlug,
            },
          })
          .then(({ data }) => data),
    })

    const duration = useMemo(() => {
      if (!assetDetails) {
        return 0
      }
      return parseInt(assetDetails.seconds ?? 0)
    }, [assetDetails])

    const assetCategory = useMemo(() => {
      if (!assetDetails || !assetDetails.category) {
        return 0
      }
      return assetDetails.category
    }, [assetDetails])


    const bpm = useMemo(() => {
      if (!assetDetails || !assetDetails.bpm || +assetDetails.bpm === 0) {
        return 0
      }
      return parseInt(assetDetails.bpm ?? 0)
    }, [assetDetails])

    const formattedDuration = useMemo(() => {
      // Duration should show at least 1 second
      return formatDuration(Math.max(duration, 1))
    }, [duration])

    const { data: waveData } = useQuery<number[]>({
      queryKey: ['waveform', assetKey],
      queryFn: async () => {
        const waveFormData: number[] =
          (await fetch(`https://p.productioncrate.com/stock-hd/audio/${assetKey}-waveform.json`)
            .then((res) => res.json())
            .catch((e) => {
              console.error('Error fetching waveform data:', e)
            })) ?? []

        const maximum = waveFormData.reduce((a: any, b: any) => Math.max(a, b), -Infinity)
        let waveNormal = waveFormData.map((v: any) => (v * 100) / maximum)

        return downsSampleSignal(waveNormal, gridLayout?4:2)
      },
      staleTime: Infinity,
    })

    const onSeek = useCallback(
      (time: number) => {
        if (!isPlaying) {
          return
        }
        seek(time)
      },
      [isPlaying, seek]
    )

    const handleToggleAudio = useCallback(() => {
      if (!isPlaying) {
        play()
      } else {
        stop()
      }
    }, [isPlaying, play, stop])

    // Stop audio when unmounting if needed
    useEffect(() => {
      return () => {
        if (stopOnUnmount) {
          stop()
        }
      }
    }, [stop, stopOnUnmount])

    // As this component will be used in a virtual grid,
    // take special attention to not reanimate when the component is re-created
    const animate = useMemo(() => {
      return audioManager.getState().currentSrc !== src
    }, [audioManager, src])

    const { addFavorite, removeFavorite, isFavorite: isFavoriteFn } = useFavorites()
    const isFavorite = isFavoriteFn(assetKey)

    const { isAuthenticating, userToken } = useUserContext()
    const { downloadOptions, dispatchAssetDownloadEvent } = useAssetDetail({ asset: assetDetails, userToken })

    return (
      <div
        className={cn(
          styles.MusicBar, 
          {
            //'music-bar tw-grid tw-grid-flow-col tw-items-center tw-justify-start tw-gap-2 ': !gridLayout,
            'music-bar tw-flex tw-items-center tw-justify-start tw-gap-2 tw-h-16': !gridLayout,
            [styles.GridLayout]: gridLayout,
          }
        )}
        onClick={() => {
          handleToggleAudio()
        }}
      >
        {/* Download button */}
        {!hideTools && !gridLayout && (
          <HoverCard openDelay={0}>
            <HoverCardTrigger asChild>
              <Button
                type="button"
                title="Download"
                variant="transparent"
                size="icon"
                className="!tw-p-1"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  // TODO: Download default option
                }}
              >
                <ArrowDownToLineIcon className="tw-h-4 tw-w-4" />
              </Button>
            </HoverCardTrigger>

            <HoverCardContent
              className="tw-max-w-min tw-border-0 !tw-bg-[var(--Primary)] tw-p-2 tw-shadow-none"
              side={'top'}
              align={'start'}
              alignOffset={-16}
            >
              <div className="tw-flex tw-flex-col tw-items-start tw-space-y-2">
                {(isLoadingAssetDetails || isAuthenticating) && (
                  <>
                    <Skeleton className="tw-h-4 tw-w-16" />
                    <Skeleton className="tw-h-4 tw-w-16" />
                    <Skeleton className="tw-h-4 tw-w-16" />
                  </>
                )}

                {!isLoadingAssetDetails && !isAuthenticating && (
                  <>
                    {downloadOptions.map((option) => (
                      <Link
                        key={`download-option-${option.url}`}
                        href={option.url}
                        prefetch={false}
                        className="tw-opacity-50 hover:tw-opacity-100"
                        onClick={(e) => {
                          e.stopPropagation()
                          dispatchAssetDownloadEvent()
                        }}
                      >
                        <div className="tw-flex tw-items-center tw-space-x-2">
                          <span className="tw-whitespace-nowrap">{option.format}</span>
                        </div>
                      </Link>
                    ))}
                  </>
                )}
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
        <div className={cn(styles.Title, 'tw-flex tw-items-center tw-space-x-2 tw-mr-auto tw-max-w-80 tw-w-full')}>
          <Button
            type="button"
            title={isPlaying ? 'Pause' : 'Play'}
            size="icon"
            variant="ghost"
            className="!tw-bg-transparent hover:!tw-bg-transparent hover:!tw-text-[var(--Primary-100)]"
          >
            {isPlaying && <PauseIcon fill="var(--Primary-100)" className="tw-h-4 tw-w-4" style={{color: 'var(--Primary-100)'}} />}
            {!isPlaying && <PlayIcon fill="var(--Primary-100)" className="tw-h-4 tw-w-4" style={{color: 'var(--Primary-100)'}} />}
          </Button>
          {!gridLayout && !!assetDetails?.level && +assetDetails.level > 0 && (
            <StarSolid className="tw-h-4 tw-w-4 tw-text-[var(--Secondary-200)]" />
          )}
          {!hideTitle && <span className="tw-font-medium tw-w-max">{asset.title}</span>}
        </div>
        {assetCategory && !gridLayout && 
        <div className="tw-text-[var(--Primary-300)] tw-min-w-32 tw-mr-auto hidden md:tw-flex">{assetCategory}</div>
        }
        {gridLayout && 
        <div className="tw-flex tw-flex-row tw-items-center tw-justify-end tw-w-full tw-gap-2">
          <div className="tw-text-[var(--Primary-300)]">{formattedDuration}</div>
          {bpm > 0 && <div className="tw-text-[var(--Primary-300)]">{bpm} bpm</div>}
        </div>
        }
        {!gridLayout && 
        <>
        <div className="tw-text-[var(--Primary-300)] tw-min-w-16">{bpm?`${bpm} bpm`:''}</div>
        <div className="tw-text-[var(--Primary-300)] ">{formattedDuration}</div>
        </>
        }
        {gridLayout && (
          <SoundWave
            waveData={waveData ?? []}
            currentTime={isPlaying ? currentTime : 0}
            duration={duration}
            onSeek={onSeek}
            className="tw-mb-4 tw-mt-4"
          />
        )}
        {!gridLayout && (
          <SoundWave
            waveData={waveData ?? []}
            currentTime={isPlaying ? currentTime : 0}
            duration={duration}
            onSeek={onSeek}
            className="tw-mr-4"
          />
        )}
        {/* Animated wave form 
        {!gridLayout  && (
          <AnimatePresence>
            {isPlaying && (
              <motion.div
              key={`${asset.title}-${src}`}
              className="tw-overflow-hidden"
              initial={{
                opacity: animate ? 0 : 1,
                transform: animate ? 'translateX(-100px)' : 'translateX(0px)',
              }}
              animate={{
                opacity: 1,
                transform: 'translateX(0px)',
              }}
              layout
            >
              <SoundWave
                waveData={waveData ?? []}
                currentTime={isPlaying ? currentTime : 0}
                duration={duration}
                onSeek={onSeek}
              />
            </motion.div>
            )}
          </AnimatePresence>
        )}
        */}
        
        {gridLayout && 
        <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-w-full">
          {assetCategory  && <div className="tw-text-[var(--Primary-300)] tw-mr-auto">{assetCategory}</div>}
          {/* Download button */}
          {gridLayout && (
            <>
              
              <HoverCard openDelay={0}>
                <HoverCardTrigger asChild>
                  <Button
                    type="button"
                    title="Download"
                    variant="transparent"
                    size="icon"
                    className="!tw-p-1 tw-flex tw-flex-row tw-items-center tw-justify-start"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      // TODO: Download default option
                    }}
                  >
                    <ArrowDownToLineIcon className="tw-h-4 tw-w-4 tw-mr-1 tw-mb-1" />
                    {gridLayout && !!assetDetails?.level && +assetDetails.level > 0 && (
                      <StarSolid className="tw-h-4 tw-w-4 tw-text-[var(--Secondary-200)] tw-mr-1 tw-mb-1" />
                    )}
                    Download 
                  </Button>
                </HoverCardTrigger>

                <HoverCardContent
                  className={`${gridLayout ? 'tw-max-w-prose' : 'tw-max-w-min'} tw-border-0 !tw-bg-[var(--Primary)] tw-p-2 tw-shadow-none`}
                  side={'top'}
                  align={'start'}
                  alignOffset={!gridLayout ? -16 : 16}
                >
                  <div className="tw-flex tw-flex-col tw-items-start tw-space-y-2">
                    {(isLoadingAssetDetails || isAuthenticating) && (
                      <>
                        <Skeleton className="tw-h-4 tw-w-16" />
                        <Skeleton className="tw-h-4 tw-w-16" />
                        <Skeleton className="tw-h-4 tw-w-16" />
                      </>
                    )}

                    {!isLoadingAssetDetails && !isAuthenticating && (
                      <>
                        {downloadOptions.map((option) => (
                          <Link
                            key={`download-option-${option.url}`}
                            href={option.url}
                            prefetch={false}
                            className="tw-opacity-50 hover:tw-opacity-100"
                            onClick={(e) => {
                              e.stopPropagation()
                              dispatchAssetDownloadEvent()
                            }}
                          >
                            <div className="tw-flex tw-items-center tw-space-x-2">
                              <span className="tw-whitespace-nowrap">{option.format}</span>
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </>
          )}
          <>
            <div className={cn(styles.Controls, 'tw-space-x-1')}>
              {children}
              {/* Favorite button */}
              {/* eslint-disable-next-line react/jsx-no-undef */}
              {features.favorites && (
                <Button
                  title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  variant="transparent"
                  size="icon"
                  className="!tw-p-1"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    isFavorite ? removeFavorite(assetKey) : addFavorite(assetKey)
                  }}
                >
                  {isFavorite ? <HeartFilledIcon className="tw-h-4 tw-w-4" /> : <HeartIcon className="tw-h-4 tw-w-4" />}
                </Button>
              )}
            </div>
          </>
        </div>
        }
      </div>
    )
  },
  isEqual
)

MusicBar.displayName = 'MusicBar'

export { MusicBar }
